"use client";

import React, { useRef } from "react";
import { ArrowRight, ChartBar as BarChart3, Building2, CircleCheck as CheckCircle2, Compass, MapPin, Search, Target, TrendingUp, Zap } from 'lucide-react';
import { Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import gsap from "gsap";
import { useGSAP } from "@gsap/react";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { ChartConfig, ChartContainer, ChartTooltipContent } from "@/components/ui/chart";
import { Separator } from "@/components/ui/separator";
gsap.registerPlugin(useGSAP, ScrollTrigger);
const metricsList = [{
  id: "m1",
  title: "Local Search Growth",
  value: "+38%",
  description: "Year-on-year surge in high-intent commercial searches across Milton Keynes postcodes.",
  badgeText: "High Growth"
}, {
  id: "m2",
  title: "Competitor Schema Gap",
  value: "76%",
  description: "Milton Keynes businesses still lacking advanced Local Schema & entity structuring.",
  badgeText: "Opportunity"
}, {
  id: "m3",
  title: "AI Search Influence",
  value: "32%",
  description: "Queries answered directly by ChatGPT, Copilot, & Google AI Overviews locally.",
  badgeText: "Emerging Focus"
}, {
  id: "m4",
  title: "High-Intent Volume",
  value: "14.2k",
  description: "Monthly local searches targeted at tech, B2B services, and logistics providers.",
  badgeText: "Lead Potential"
}];
const keywordOpportunities = [{
  keyword: "SEO Consultant Milton Keynes",
  volume: "480 / mo",
  difficulty: "Medium",
  intent: "High Commercial",
  strategy: "Target hyper-local landing pages backed by structured entity data and verified case studies."
}, {
  keyword: "B2B Marketing Services MK",
  volume: "1,200 / mo",
  difficulty: "Low-Medium",
  intent: "Commercial",
  strategy: "Build content clusters focusing on Linford Wood and Central Milton Keynes commercial hubs."
}, {
  keyword: "Logistics SEO Milton Keynes",
  volume: "880 / mo",
  difficulty: "Low",
  intent: "Transactional",
  strategy: "Optimize supply-chain intent for enterprise parks like Magna Park and Kiln Farm."
}, {
  keyword: "Local Business SEO Bucks",
  volume: "1,600 / mo",
  difficulty: "Medium",
  intent: "High Intent",
  strategy: "Enhance Google Business Profile signals with authoritative regional citations."
}];
const neighboringCities = [{
  cityName: "Northampton",
  distance: "19 miles north",
  focus: "E-commerce & Distribution Hub SEO",
  description: "High demand for regional logistics search strategy and B2B lead acquisition.",
  linkPath: "/LocalSEOPage"
}, {
  cityName: "Bedford",
  distance: "18 miles east",
  focus: "Local Services & Tech Consultancy SEO",
  description: "Growing commercial market with low competition in organic AI Search answers.",
  linkPath: "/LocalSEOPage"
}, {
  cityName: "Luton",
  distance: "23 miles south",
  focus: "Aviation & Industrial SEO Strategy",
  description: "Targeted organic campaigns for international supply and B2B corporate services.",
  linkPath: "/LocalSEOPage"
}, {
  cityName: "Oxford",
  distance: "38 miles west",
  focus: "Life Sciences & Tech Innovation SEO",
  description: "High-authority technical SEO strategy for university scale-ups and innovation centers.",
  linkPath: "/LocalSEOPage"
}];
const chartData = [{
  month: "Jan",
  searchVolume: 8200,
  aiQueries: 1400
}, {
  month: "Feb",
  searchVolume: 8900,
  aiQueries: 1800
}, {
  month: "Mar",
  searchVolume: 9400,
  aiQueries: 2300
}, {
  month: "Apr",
  searchVolume: 10200,
  aiQueries: 2900
}, {
  month: "May",
  searchVolume: 11500,
  aiQueries: 3600
}, {
  month: "Jun",
  searchVolume: 12800,
  aiQueries: 4200
}, {
  month: "Jul",
  searchVolume: 14200,
  aiQueries: 5100
}];
const chartConfig: ChartConfig = {
  searchVolume: {
    label: "Traditional Search Demand",
    color: "hsl(220 80% 30%)"
  },
  aiQueries: {
    label: "AI Overviews & Direct Queries",
    color: "hsl(340 75% 65%)"
  }
};
export default function MiltonKeynes() {
  const containerRef = useRef<HTMLDivElement>(null);
  useGSAP(() => {
    const el = containerRef.current;
    if (!el) return;
    const mm = gsap.matchMedia();
    mm.add("(prefers-reduced-motion: no-preference)", () => {
      gsap.set(".gsap-reveal", {
        y: 30
      });
      gsap.to(".gsap-reveal", {
        opacity: 1,
        y: 0,
        duration: 0.7,
        stagger: 0.1,
        ease: "power2.out",
        scrollTrigger: {
          trigger: el,
          start: "top 80%",
          once: true
        }
      });
    });
    mm.add("(prefers-reduced-motion: reduce)", () => {
      gsap.set(".gsap-reveal", {
        opacity: 1,
        y: 0
      });
    });
    const refresh = () => ScrollTrigger.refresh();
    requestAnimationFrame(refresh);
    if (document.readyState === "complete") refresh();else window.addEventListener("load", refresh, {
      once: true
    });
    document.fonts?.ready.then(refresh);
  }, {
    scope: containerRef
  });
  return <section id="miltonkeynesmarketinsights" data-nav="light" className="relative w-full bg-background py-20 text-foreground">
      <div ref={containerRef} className="container mx-auto px-4 md:px-6">
        {}
        <div className="gsap-reveal mx-auto max-w-3xl text-center" style={{
        opacity: 0
      }}>
          <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-border bg-muted/60 px-3.5 py-1 text-xs font-medium text-foreground">
            <MapPin className="h-3.5 w-3.5 text-primary" />
            <span>Milton Keynes & Buckinghamshire SEO Landscape</span>
          </div>

          <h2 className="text-2xl font-bold tracking-tight text-foreground sm:text-3xl lg:text-4xl">
            Milton Keynes Search Trends & Local Growth Opportunities
          </h2>

          <p className="mt-4 text-base text-muted-foreground md:text-lg">
            As an independent freelance SEO consultant, I analyze real organic search demand across Milton Keynes. Below is a breakdown of keyword expansion, competitor gaps, and actionable SEO strategies to help your business capture high-intent local revenue.
          </p>
        </div>

        {}
        <div className="mt-12 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {metricsList.map((item, i) => <div key={item.id} data-index={i} className="gsap-reveal" style={{
          opacity: 0
        }}>
              <Card className="h-full border border-border bg-card shadow-xs transition-shadow hover:shadow-md">
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <Badge variant="secondary" className="font-normal text-xs">
                      {item.badgeText}
                    </Badge>
                    <BarChart3 className="h-4 w-4 text-muted-foreground" />
                  </div>
                  <CardTitle className="text-3xl font-bold text-primary mt-2">
                    {item.value}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <h3 className="text-sm font-semibold text-card-foreground">
                    {item.title}
                  </h3>
                  <p className="mt-1 text-xs text-muted-foreground leading-relaxed">
                    {item.description}
                  </p>
                </CardContent>
              </Card>
            </div>)}
        </div>

        {}
        <div className="gsap-reveal mt-12 rounded-xl border border-border bg-card p-6 shadow-sm md:p-8" style={{
        opacity: 0
      }}>
          <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <div>
              <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-accent">
                <TrendingUp className="h-4 w-4" />
                <span>Search Growth Trajectory</span>
              </div>
              <h3 className="text-xl font-bold text-card-foreground md:text-2xl mt-1">
                Milton Keynes Search Demand vs. AI Query Expansion
              </h3>
              <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
                Commercial intent searches in Milton Keynes have grown steadily, with a significant shift toward conversational AI answers and Google AI Overviews.
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-4 text-xs">
              <div className="flex items-center gap-2">
                <div className="h-3 w-3 rounded-xs bg-primary" />
                <span className="text-muted-foreground">Standard Search</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="h-3 w-3 rounded-xs bg-accent" />
                <span className="text-muted-foreground">AI Overviews</span>
              </div>
            </div>
          </div>

          <div className="mt-8 h-72 w-full">
            <ChartContainer config={chartConfig} className="h-full w-full">
              <AreaChart data={chartData} margin={{
              top: 10,
              right: 10,
              left: -20,
              bottom: 0
            }}>
                <defs>
                  <linearGradient id="fillSearch" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="var(--color-searchVolume)" stopOpacity={0.4} />
                    <stop offset="95%" stopColor="var(--color-searchVolume)" stopOpacity={0.0} />
                  </linearGradient>
                  <linearGradient id="fillAi" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="var(--color-aiQueries)" stopOpacity={0.4} />
                    <stop offset="95%" stopColor="var(--color-aiQueries)" stopOpacity={0.0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" vertical={false} className="stroke-border/60" />
                <XAxis dataKey="month" tickLine={false} axisLine={false} className="text-xs text-muted-foreground" />
                <YAxis tickLine={false} axisLine={false} className="text-xs text-muted-foreground" />
                <Tooltip content={<ChartTooltipContent />} />
                <Area type="monotone" dataKey="searchVolume" stroke="var(--color-searchVolume)" strokeWidth={2} fillOpacity={1} fill="url(#fillSearch)" />
                <Area type="monotone" dataKey="aiQueries" stroke="var(--color-aiQueries)" strokeWidth={2} fillOpacity={1} fill="url(#fillAi)" />
              </AreaChart>
            </ChartContainer>
          </div>
        </div>

        {}
        <div className="mt-16">
          <div className="gsap-reveal text-center md:text-left" style={{
          opacity: 0
        }}>
            <Badge variant="outline" className="mb-2">
              Competitive Analysis
            </Badge>
            <h3 className="text-2xl font-bold text-foreground sm:text-3xl">
              High-Value Milton Keynes Target Keywords
            </h3>
            <p className="mt-2 text-sm text-muted-foreground max-w-2xl">
              I identify specific search queries where local businesses can outrank national agencies by deploying hyper-focused technical structure and rich context.
            </p>
          </div>

          <div className="mt-8 grid grid-cols-1 gap-6 md:grid-cols-2">
            {keywordOpportunities.map((item, i) => <div key={item.keyword} data-index={i} className="gsap-reveal" style={{
            opacity: 0
          }}>
                <Card className="h-full border border-border bg-card shadow-xs">
                  <CardHeader className="pb-3">
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <div className="flex items-center gap-2">
                          <Search className="h-4 w-4 text-primary" />
                          <CardTitle className="text-base font-semibold text-card-foreground">
                            {item.keyword}
                          </CardTitle>
                        </div>
                        <CardDescription className="mt-1 text-xs">
                          Volume: <span className="font-medium text-foreground">{item.volume}</span> | Intent: <span className="font-medium text-foreground">{item.intent}</span>
                        </CardDescription>
                      </div>
                      <Badge variant="secondary" className="shrink-0 text-xs">
                        {item.difficulty} Difficulty
                      </Badge>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="rounded-lg bg-muted/50 p-3 text-xs text-muted-foreground">
                      <strong className="font-semibold text-foreground">Action Plan: </strong>
                      {item.strategy}
                    </div>
                  </CardContent>
                </Card>
              </div>)}
          </div>
        </div>

        <Separator className="my-16" />

        {}
        <div className="gsap-reveal" style={{
        opacity: 0
      }}>
          <div className="flex flex-col gap-2 md:flex-row md:items-end md:justify-between">
            <div>
              <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-primary">
                <Compass className="h-4 w-4" />
                <span>Regional Authority Network</span>
              </div>
              <h3 className="text-2xl font-bold text-foreground sm:text-3xl mt-1">
                SEO Services Across Neighboring Hubs
              </h3>
              <p className="mt-2 text-sm text-muted-foreground max-w-2xl">
                I help businesses establish regional search dominance across Buckinghamshire, Northamptonshire, and Oxfordshire through targeted local SEO strategies.
              </p>
            </div>
            <div className="mt-4 md:mt-0">
              <Button variant="outline" asChild size="sm">
                <Link to="/LocalSEOPage" className="gap-2">
                  <span>Explore All Local SEO Hubs</span>
                  <ArrowRight className="h-4 w-4" />
                </Link>
              </Button>
            </div>
          </div>

          <div className="mt-8 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {neighboringCities.map((city, i) => <div key={city.cityName} data-index={i}>
                <Card className="h-full border border-border bg-card shadow-xs transition-colors hover:border-primary/50">
                  <CardHeader className="pb-2">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                        <Building2 className="h-3.5 w-3.5 text-primary" />
                        <span>{city.distance}</span>
                      </div>
                    </div>
                    <CardTitle className="text-lg font-bold text-card-foreground mt-2">
                      {city.cityName}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="pb-4">
                    <p className="text-xs font-medium text-accent">
                      {city.focus}
                    </p>
                    <p className="mt-2 text-xs text-muted-foreground leading-relaxed">
                      {city.description}
                    </p>
                  </CardContent>
                  <div className="px-6 pb-6 pt-0 mt-auto">
                    <Button variant="ghost" size="sm" asChild className="w-full justify-between px-0 hover:bg-transparent text-primary hover:text-primary/80">
                      <Link to={city.linkPath} className="text-xs font-medium">
                        <span>{city.cityName} SEO Strategy</span>
                        <ArrowRight className="h-3.5 w-3.5" />
                      </Link>
                    </Button>
                  </div>
                </Card>
              </div>)}
          </div>
        </div>

        {}
        <div className="gsap-reveal mt-16 rounded-2xl bg-muted/60 border border-border p-8 text-center md:p-12" style={{
        opacity: 0
      }}>
          <div className="mx-auto max-w-2xl">
            <Badge variant="default" className="mb-4 bg-primary text-primary-foreground">
              Direct Consultant Access
            </Badge>
            <h3 className="text-2xl font-bold text-foreground sm:text-3xl">
              Ready to Expand Your Search Visibility in Milton Keynes?
            </h3>
            <p className="mt-3 text-sm text-muted-foreground md:text-base">
              You work directly with me—no account managers, no junior staff, and no generic agency templates. Let us audit your Milton Keynes search opportunities today.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-4">
              <Button size="lg" asChild className="w-full sm:w-auto">
                <Link to="/ContactPage" className="gap-2">
                  <span>Book a Free SEO Consultation</span>
                  <ArrowRight className="h-4 w-4" />
                </Link>
              </Button>
              <Button variant="outline" size="lg" asChild className="w-full sm:w-auto">
                <Link to="/CaseStudiesPage">
                  View Real Case Studies
                </Link>
              </Button>
            </div>
            <div className="mt-6 flex items-center justify-center gap-6 text-xs text-muted-foreground">
              <span className="flex items-center gap-1.5">
                <CheckCircle2 className="h-3.5 w-3.5 text-primary" />
                Tailored Local Strategy
              </span>
              <span className="flex items-center gap-1.5">
                <CheckCircle2 className="h-3.5 w-3.5 text-primary" />
                Transparent Deliverables
              </span>
            </div>
          </div>
        </div>
      </div>
    </section>;
}